'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Mail, Gift, CheckCircle, Loader2 } from 'lucide-react';
import { consentSettled, claimPromoSlot, releasePromoSlot, onPromoSlotChange } from '@/lib/ui/popupQueue';

// Identifies this popup as the owner of the single promotional slot.
const POPUP_ID = 'email-capture';

interface EmailCapturePopupProps {
  discountPercent?: number;
  discountCode?: string;
  delaySeconds?: number;
  storageKey?: string;
}

/**
 * Email Capture Popup Component
 *
 * Shows a popup asking for email in exchange for a discount code.
 * Appears after a delay for new visitors who haven't seen it before.
 *
 * Features:
 * - Delay before showing (default 10 seconds)
 * - Only shows once per visitor (stored in localStorage)
 * - Submits email to API for storage and future marketing
 * - Shows discount code after successful submission
 * - Mobile responsive
 */
const EmailCapturePopup: React.FC<EmailCapturePopupProps> = ({
  discountPercent: propDiscountPercent,
  discountCode: propDiscountCode,
  delaySeconds: propDelaySeconds,
  storageKey = 'email-capture-shown'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  // Honeypot — hidden from humans; bots that fill every field are rejected
  // server-side. Stays '' for real users.
  const [honeypot, setHoneypot] = useState('');
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null);
  // Set on close. Releasing the promo slot notifies every subscriber — including
  // this popup's own retry listener — so without this guard it re-claimed the
  // slot and re-opened the instant it was closed.
  const dismissedRef = useRef(false);
  const [settings, setSettings] = useState({
    delaySeconds: propDelaySeconds ?? 10,
    discountPercent: propDiscountPercent ?? 10,
    discountCode: propDiscountCode ?? 'WELCOME10'
  });

  // Check if popup has been shown/dismissed before
  const hasShownBefore = useCallback(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(storageKey) !== null;
  }, [storageKey]);

  // Fetch popup settings to check if enabled and get config
  useEffect(() => {
    // Skip for search engine crawlers
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('googlebot') || ua.includes('bingbot') || ua.includes('yandex') || ua.includes('baiduspider')) {
      setIsEnabled(false);
      return;
    }
    const fetchPopupSettings = async () => {
      try {
        const response = await fetch('/api/v2/commerce/popup-settings');
        const data = await response.json();
        if (data.success && data.data) {
          setIsEnabled(data.data.enable_email_capture_popup ?? true);
          // Use API settings if no props were provided
          setSettings(prev => ({
            delaySeconds: propDelaySeconds ?? data.data.email_capture_delay_seconds ?? prev.delaySeconds,
            discountPercent: propDiscountPercent ?? data.data.email_capture_discount_percent ?? prev.discountPercent,
            discountCode: propDiscountCode ?? data.data.email_capture_discount_code ?? prev.discountCode
          }));
        } else {
          setIsEnabled(true); // Default to enabled
        }
      } catch (error) {
        console.error('Failed to fetch popup settings:', error);
        setIsEnabled(true); // Default to enabled on error
      }
    };

    fetchPopupSettings();
  }, [propDelaySeconds, propDiscountPercent, propDiscountCode]);

  // Show popup after delay — but never on top of the cookie banner or another
  // promotional popup. Previously this fired on a bare 10s timer regardless of
  // what else was on screen, which stacked three overlays on a first visit.
  useEffect(() => {
    // Don't show if disabled in admin settings
    if (isEnabled === false) return;
    // Wait until we know if it's enabled
    if (isEnabled === null) return;
    if (hasShownBefore()) return;

    let timer: ReturnType<typeof setTimeout> | null = null;

    const attempt = () => {
      if (dismissedRef.current || hasShownBefore()) return;
      // Cookie consent must be answered first, and only one promo at a time.
      if (!consentSettled()) return;
      if (!claimPromoSlot(POPUP_ID)) return;
      setIsVisible(true);
    };

    timer = setTimeout(attempt, settings.delaySeconds * 1000);
    // Re-try when another popup frees the slot, so a visitor who dismisses the
    // other one still sees this offer rather than it being lost for the session.
    const unsub = onPromoSlotChange(attempt);

    return () => {
      if (timer) clearTimeout(timer);
      unsub();
      releasePromoSlot(POPUP_ID);
    };
  }, [settings.delaySeconds, hasShownBefore, isEnabled]);

  const handleClose = () => {
    // Record the dismissal BEFORE releasing the slot: the release notifies
    // listeners synchronously, and this popup's own listener checks these.
    dismissedRef.current = true;
    try {
      localStorage.setItem(storageKey, Date.now().toString());
    } catch {
      /* storage blocked — dismissedRef still prevents a re-open this page view */
    }
    setIsVisible(false);
    releasePromoSlot(POPUP_ID);
  };

  // Escape closes the popup
  useEffect(() => {
    if (!isVisible) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && handleClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/email-subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          source: 'popup',
          couponCode: settings.discountCode,
          company_website: honeypot, // honeypot — always '' for real users
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);
        // Store email in localStorage for abandoned cart tracking
        localStorage.setItem('guest_email', email.trim());
        localStorage.setItem(storageKey, Date.now().toString());
      } else {
        setError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(settings.discountCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = settings.discountCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-[9998] backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Popup */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 font-al-body" role="dialog" aria-modal="true" aria-label="Email offer">
        <div className="relative w-full max-w-[calc(100vw-24px)] sm:max-w-md max-h-[calc(100vh-24px)] overflow-y-auto rounded-sm border border-al-line bg-al-ink-soft text-al-cream shadow-2xl shadow-black/60 animate-in zoom-in-95 duration-300">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 rounded-full p-2 text-al-cream/70 transition-colors hover:bg-al-cream/10 hover:text-al-gold"
            aria-label="Close popup"
          >
            <X className="h-5 w-5" />
          </button>

          {!isSubmitted ? (
            <>
              {/* Header */}
              <div className="border-b border-al-line bg-[radial-gradient(ellipse_at_top,rgba(123,45,59,0.55),transparent_75%)] p-6 text-center sm:p-8">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-al-gold/50 text-al-gold">
                  <Gift className="h-7 w-7" />
                </div>
                <h2 className="font-al-display text-3xl sm:text-4xl">
                  Get {settings.discountPercent}% Off
                </h2>
                <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-al-gold">
                  Your first order
                </p>
              </div>

              {/* Form */}
              <div className="p-4 sm:p-6">
                <p className="mb-5 text-center text-sm leading-relaxed text-al-cream/70">
                  Join The Aarohi Letter for first looks at new collections, festive edits and members-only offers.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot — invisible to humans, irresistible to form-filling bots. */}
                  <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
                    <label htmlFor="company_website_popup">Company website (leave blank)</label>
                    <input maxLength={80}
                      id="company_website_popup"
                      name="company_website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-al-cream/40" />
                      <input maxLength={254}
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="al-field pl-10"
                        disabled={isSubmitting}
                        autoComplete="email"
                      />
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-al-rose" role="alert">{error}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="al-btn-primary w-full"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Subscribing...
                      </>
                    ) : (
                      `Get My ${settings.discountPercent}% Off`
                    )}
                  </button>
                </form>

                <p className="mt-4 text-center text-xs leading-relaxed text-al-cream/45">
                  By subscribing, you agree to receive marketing emails including promotions, new arrivals and styling notes. Unsubscribe anytime via the link in each email. See our <a href="/privacy" className="underline hover:text-al-gold">Privacy Policy</a>.
                </p>
              </div>
            </>
          ) : (
            /* Success State */
            <div className="p-6 sm:p-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-al-gold/50">
                <CheckCircle className="h-8 w-8 text-al-gold" />
              </div>

              <h2 className="mb-2 font-al-display text-3xl">
                You're In!
              </h2>
              <p className="mb-6 text-sm text-al-cream/70">
                Here's your exclusive {settings.discountPercent}% discount code:
              </p>

              {/* Coupon Code */}
              <div className="mb-6 rounded-sm border border-dashed border-al-gold/50 p-4">
                <div className="flex items-center justify-center gap-3">
                  <span className="font-mono text-xl font-bold tracking-wider text-al-gold sm:text-2xl">
                    {settings.discountCode}
                  </span>
                  <button
                    onClick={copyCode}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      copied
                        ? 'bg-al-gold text-al-ink'
                        : 'bg-al-rose text-al-ink hover:bg-al-gold'
                    }`}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <p className="mb-6 text-sm text-al-cream/55">
                Use this code at checkout. Valid for your first order only.
              </p>

              <button
                onClick={handleClose}
                className="al-btn-primary w-full"
              >
                Start Shopping
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailCapturePopup;
